import { useState } from 'react';
import { Box, Button, Card, Typography } from '@mui/material';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton, RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stForceExpireRedPacket,
  StRedPacketPacket,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import {
  StActionButton,
  StActionRow,
  StIdCell,
  StNameIdCell,
  StStatusChip,
  stFormatDateTime,
  stText,
} from '../StListUi';
import { socialTradingLedgerEntriesByPacketPath } from '../redpacketNav';
import './init';

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <TextInput
        source="roomName"
        label={t('module.socialTrading.colRoomName')}
        alwaysOn
        resettable
      />
      <SelectInput
        source="status"
        label={t('module.socialTrading.colStatus')}
        choices={[
          { id: 'ACTIVE', name: 'ACTIVE' },
          { id: 'CLAIMED_OUT', name: 'CLAIMED_OUT' },
          { id: 'EXPIRED', name: 'EXPIRED' },
        ]}
        alwaysOn
        resettable
      />
    </Filter>
  );
};

export default function SocialTradingRedpacketPacketsList() {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [expireTarget, setExpireTarget] = useState<StRedPacketPacket | null>(null);
  const [expiring, setExpiring] = useState(false);

  const confirmForceExpire = async () => {
    if (!expireTarget) return;
    setExpiring(true);
    try {
      await stForceExpireRedPacket(expireTarget.id);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setExpireTarget(null);
      refresh();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setExpiring(false);
    }
  };

  return (
    <PermissionGuard resource="st-redpacket-packets" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.redpacketPackets')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.redpacketPackets') },
          ]}
        />

        <Card sx={{ mt: 2, pb: 2 }}>
          <List
            resource={Resources.socialTradingRedpacketPackets}
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<ListFilter />}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('module.socialTrading.emptyPackets')}
                  </Typography>
                }
              >
                <FunctionField
                  label={t('module.socialTrading.colPacketId')}
                  render={(row: StRedPacketPacket) => (
                    <StIdCell value={row.id} to={socialTradingLedgerEntriesByPacketPath(row.id)} />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colBlessings')}
                  render={(row: StRedPacketPacket) => stText(row.name)}
                />
                <FunctionField
                  label={t('module.socialTrading.colRoom')}
                  render={(row: StRedPacketPacket) => (
                    <StNameIdCell name={row.roomName} id={row.roomId} />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colTotalAmt')}
                  render={(row: StRedPacketPacket) => stText(row.totalAmount)}
                />
                <FunctionField
                  label={t('module.socialTrading.colClaimedAmt')}
                  render={(row: StRedPacketPacket) => stText(row.claimedAmount)}
                />
                <FunctionField
                  label={t('module.socialTrading.colRefundedAmount')}
                  render={(row: StRedPacketPacket) => stText(row.refundedAmount)}
                />
                <FunctionField
                  label={t('module.socialTrading.colClaimCount')}
                  render={(row: StRedPacketPacket) => `${row.claimCount}/${row.claimCap}`}
                />
                <FunctionField
                  label={t('module.socialTrading.colCreatedAt')}
                  render={(row: StRedPacketPacket) => stText(stFormatDateTime(row.createdAt))}
                />
                <FunctionField
                  label={t('module.socialTrading.colExpiresAt')}
                  render={(row: StRedPacketPacket) => stText(stFormatDateTime(row.expiresAt))}
                />
                <FunctionField
                  label={t('module.socialTrading.colStatus')}
                  render={(row: StRedPacketPacket) => <StStatusChip status={row.status} />}
                />
                <FunctionField
                  label={t('module.socialTrading.actions')}
                  textAlign="right"
                  render={(row: StRedPacketPacket) => (
                    <StActionRow>
                      <StActionButton
                        component={RouterLink}
                        href={socialTradingLedgerEntriesByPacketPath(row.id)}
                      >
                        {t('module.socialTrading.viewLedger')}
                      </StActionButton>
                      {row.status === 'ACTIVE' ? (
                        <PermissionButton resource="st-redpacket-packets" action="update">
                          <StActionButton color="warning" onClick={() => setExpireTarget(row)}>
                            {t('module.socialTrading.forceExpireRedPacket')}
                          </StActionButton>
                        </PermissionButton>
                      ) : null}
                    </StActionRow>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <ConfirmDialog
          open={!!expireTarget}
          onClose={() => !expiring && setExpireTarget(null)}
          title={t('module.socialTrading.confirmForceExpireRedPacket')}
          content={
            expireTarget ? (
              <>
                <Typography variant="body2">
                  {expireTarget.name}
                  <br />
                  <Typography component="span" variant="caption">
                    {expireTarget.id}
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('module.socialTrading.confirmForceExpireRedPacketBody')}
                </Typography>
              </>
            ) : null
          }
          action={
            <PermissionButton resource="st-redpacket-packets" action="update">
              <Button
                variant="contained"
                color="error"
                disabled={expiring}
                onClick={confirmForceExpire}
              >
                {t('module.socialTrading.forceExpireRedPacket')}
              </Button>
            </PermissionButton>
          }
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
